import pageStyles from "./pathPage.module.scss";
import { useTranslation } from "next-i18next";
import { useEffect } from "react";
import { trackEvent } from "@kunduz/utility-analytics";
import Sticky from "../../../../common/components/sticky";
import { ILearning } from "@kunduz/data-access-learning-api";
import { NextSeo } from "next-seo";
import { LearningSimiliarCourses } from "../../components/learningSimilarCourses/learningSimilarCourses";
import QuestionSideCtaLoader, {
  SideCTAModes,
} from "../../../../common/components/sideCta";
import { LearningBreadcrumb } from "../../components/learningBreadcrumb/learningBreadcrumb";
import Link from "next/link";
import { LearningPathOrModuleItem } from "../../components/pathOrModuleItem/learningPathOrModuleItem";
import { getContentUrl, ReservedWords } from "@kunduz/utility-common";
import { LearningPagination } from "../../components/pagination/learningPagination";
import {
  BlogQIMobileCta,
  WebsiteTrFooter,
  DynamicHeader,
  HeaderBannerWithContext,
} from "@kunduz/feature-landing-pages";
import { IContent } from "@kunduz/data-access-cms-api";

const PathPage = (props: PathPageProps) => {
  const { t } = useTranslation("kunduzcom_learning");

  useEffect(() => {
    trackEvent("Student Learning Path Page Viewed", {
      learning_path: props.pageMeta.name,
    });
  }, []);

  return (
    <>
      <NextSeo
        title={props.pageMeta?.seo_meta?.meta_title}
        description={props.pageMeta?.seo_meta?.meta_desc}
        canonical={getContentUrl({
          lang: props.lang,
          domain: process.env.NEXT_PUBLIC_DOMAIN_PREFIX,
          contentType: ReservedWords.TR_CONTENT_PATH_DETAIL,
          segments: [props.pathDetailSlug],
        })}
        robotsProps={{
          maxImagePreview: "large",
        }}
      />

      {props.headerBanner && (
        <HeaderBannerWithContext
          label={props.headerBanner.content}
          buttonLabel={props.headerBanner.cta_title}
          url={props.headerBanner.cta_link}
        />
      )}

      <DynamicHeader
        lang={props.lang}
        headerType="student"
        headerItems={props.headerItems}
      />

      <BlogQIMobileCta />

      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.breadcrumb}>
          <LearningBreadcrumb
            items={props.pageMeta.breadcrumb}
            homePageLabel={t("learning_breadcrumb_homepage_label")}
            lang={props.lang}
            linkAs={Link}
          />
        </div>
        <main className={pageStyles.content}>
          <div className={pageStyles.headersContainer}>
            <h1>
              {t("learning_path_page_title", {
                path_name: props.pageMeta.name,
              })}
            </h1>
            {props.pageMeta.seo_meta?.sub_title && (
              <div className={pageStyles.subTitle}>
                {props.pageMeta.seo_meta.sub_title}
              </div>
            )}
            {props.pageMeta.seo_meta?.short_desc && (
              <div className={pageStyles.shortDesc}>
                {props.pageMeta.seo_meta.short_desc}
              </div>
            )}
          </div>
          <div className={pageStyles.moduleItemsContainer}>
            {props.modules?.results?.map((module) => (
              <LearningPathOrModuleItem
                key={module.id}
                name={module.name}
                slug={getContentUrl({
                  lang: props.lang,
                  contentType: ReservedWords.TR_CONTENT_MODULE,
                  segments: [module.slug],
                })}
                description={module.seo_meta.sub_title}
                isMobile={props.isMobile}
                numberOfUnits={module.stats_meta.number_of_units}
                numberOfVideos={module.stats_meta.number_of_videos}
                videosLabel={t(
                  "learning_number_of_video_components_icon_label",
                )}
                unitLabel={t("learning_number_of_modules_icon_label")}
                numberOfSubjects={module.stats_meta.number_of_subjects}
                subjectsLabel={t("learning_number_of_concepts_icon_label")}
                goPathLabel={t("learning_learning_path_item_view_unit")}
                img={module.logo}
                lang={props.lang}
                linkAs={Link}
              />
            ))}
          </div>
          {props.modules?.count > props.perPage && (
            <LearningPagination
              currentPage={props.currentPage}
              perPage={props.perPage}
              total={props.modules.count}
              previousText={t("learning_pagination_previous_label")}
              nextText={t("learning_pagination_next_label")}
              lang={props.lang}
              linkAs={Link}
            />
          )}

          <LearningSimiliarCourses
            isMobile={props.isMobile}
            goPathLabel={t("learning_learning_path_item_view_unit")}
            numberOfSubjectsLabel={t("learning_number_of_concepts_icon_label")}
            numberOfVideosLabel={t(
              "learning_number_of_video_components_icon_label",
            )}
            title={t("learning_similar_contents_header_paths")}
            id={props.pageMeta.id}
            type="paths"
            lang={props.lang}
            linkAs={Link}
          />

          {props.pageMeta.seo_meta?.long_desc && (
            <div className={pageStyles.longDescription}>
              {props.pageMeta.seo_meta.long_desc}
            </div>
          )}
        </main>
        <div className={pageStyles.sticky}>
          <Sticky top={100}>
            <QuestionSideCtaLoader
              isMobile={props.isMobile}
              lang={props.lang}
              mode={SideCTAModes.LEARNING}
              translationFile="kunduzcom_learning"
            />
          </Sticky>
        </div>
      </div>

      <WebsiteTrFooter />
    </>
  );
};

export interface PathPageProps {
  isMobile: boolean;
  isAuth: boolean;
  lang: "tr" | "en";
  modules: {
    count: number;
    results: ILearning.LearningPathModule[];
  };
  perPage: number;
  currentPage: number;
  pageMeta: ILearning.PathDetailResponse;
  pathDetailSlug: string;
  headerItems: IContent.HeaderItemsResponse;
  headerBanner?: IContent.HeaderBannerResponse;
}

export default PathPage;
